import { createContext, useCallback, useContext, useMemo } from "react";

const PlayerTableCtx = createContext<string[]>([]);

export const PlayerTableProvider = PlayerTableCtx.Provider;

/** Resolve a single interned player index into its display name. */
export function usePlayerName(idx: number | null | undefined): string | null {
  const table = useContext(PlayerTableCtx);
  if (idx === null || idx === undefined) return null;
  return table[idx] ?? null;
}

/** Access the full intern table (e.g. to populate a dropdown). */
export function usePlayerTable(): string[] {
  return useContext(PlayerTableCtx);
}

/**
 * Returns a stable resolver: `(idx) => name | null`. Preferred over
 * `usePlayerName` when resolving many indices in a single render or when
 * passing a resolver down as a prop.
 */
export function usePlayerResolver(): (idx: number | null | undefined) => string | null {
  const table = useContext(PlayerTableCtx);
  return useCallback(
    (idx: number | null | undefined) =>
      idx === null || idx === undefined ? null : table[idx] ?? null,
    [table],
  );
}

/** Memoized sorted list of `{index, name}` pairs — useful for dropdowns. */
export function usePlayerOptions(): { index: number; name: string }[] {
  const table = useContext(PlayerTableCtx);
  return useMemo(
    () =>
      table
        .map((name, index) => ({ index, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [table],
  );
}
